import { ref, computed, watch, onMounted, unref } from 'vue';
import type { Ref } from 'vue';
import { useNotify } from '@/composables';
import yardSpotService from '@satellite/services/yardSpot-service';

type Spot = {
  id?: string;
  code?: string;
  spotAreaId?: string;
};

export interface UseYardSpotPresenterOptions {
  spotId?: Ref<string | null | undefined>;
  spot?: Ref<Spot | null | undefined>;
}

export function useYardSpotPresenter(
  { spotId, spot }: UseYardSpotPresenterOptions = {},
  lazy = false
) {
  const notify = useNotify();

  const fetchedSpot = ref(null);
  const isLoading = ref(false);

  const currentSpot = computed(() => {
    return { ...spot?.value, ...fetchedSpot.value };
  });

  const spotCode = computed(() => {
    if (!currentSpot.value) {
      return null;
    }

    return currentSpot.value.code || null;
  });

  const areaId = computed(() => {
    if (!currentSpot.value) {
      return null;
    }

    return currentSpot.value.spotAreaId || null;
  });

  const fetchSpot = async id => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await yardSpotService.getOneById(id);
      fetchedSpot.value = response;
    } catch (error) {
      notify('Error fetching spot', 'error');
      fetchedSpot.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = () => {
    fetchedSpot.value = null;

    const currentSpotIdValue = unref(spotId);
    const currentSpotValue = unref(spot);
    const id = currentSpotIdValue || currentSpotValue?.id;

    if (id) {
      fetchSpot(id);
    }
  };

  watch([() => unref(spotId), () => unref(spot)?.id], () => refresh());

  onMounted(() => {
    /**
     * We also check for the spotAreaId because we need that info to pass to the
     * YardAreaPresenter component, so it can fetch and render the correct area name.
     */
    if ((!lazy && spotId) || !unref(spot)?.spotAreaId) {
      refresh();
    }
  });

  return {
    isLoading,
    spotCode,
    areaId,

    fetchSpot,
    refresh
  };
}
